/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import Hls from 'hls.js';
import { Map } from 'immutable';
import axios, { AxiosError, AxiosResponse } from 'axios';
import {
  Editor,
  EditorState,
  Modifier,
  ContentBlock,
  ContentState,
  CompositeDecorator,
  SelectionState,
} from 'draft-js';
import { Button, Dropdown, Row, Col, Space, MenuProps } from 'antd';
import {
  InfoCircleOutlined,
  DownOutlined,
  HighlightOutlined,
  VerticalAlignMiddleOutlined,
  TagOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { RegionInfo, SegmentInfo, SegmentWordInfo, WaveformData, WordInfo } from './models';
import { RouteComponentProps, withRouter } from 'react-router';
import ErrorMessage from './components/ErrorMessage';
import { StreamingSpeechHandler } from './streamingSpeech';
import transcript_pb, { Annotation, TranscriptMetadata } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import { LanguageCodeOption } from './speech';
import VideoPlayer from './components/Player';
import Waveform from './components/Waveform';
import TranscriptMetadataDisplay from './components/TranscriptMetadataDisplay';
import { withAnonymousAuth, AuthContextProps, withAuth } from './LocalAuthWrapper';
import { durationObjectToFloat } from './helpers';
import TranscriptService from './TranscriptService';
import KeyboardUtils from './keyboard-utils';
import DraftEditorWrapper from './DraftEditorWrapper';
import SpeechEditorWrapper from './SpeechEditorWrapper';
import EditorHeaderControls from './components/EditorHeaderControls';
import SpeakerModal from './components/SpeakerModal';
import Layout from './components/Layout';
import './Editor.css';
import DecoratorSegment from './components/Segment';
import * as draftUtils from './draft-utils';
import { BasicError, TranscriptExportFormat } from './types';
import AnnotationWaveform from './components/AnnotationWaveform';

enum EntityType {
  SYNC_WORD = 'SYNC_WORD',
  SYNC_SEGMENT = 'SYNC_SEGMENT',
}

const syncSegmentStrategy = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState,
) => {
  contentBlock.findEntityRanges((char) => {
    const entityKey = char.getEntity();
    if (entityKey === null) return false;

    const entity = contentState.getEntity(entityKey);
    if (entity.getType() !== EntityType.SYNC_SEGMENT) {
      return false;
    }
    return true;
  }, callback);
};

const defaultLanguageCode = () => {
  const languageCode = localStorage.getItem('tiro:submittranscript:defaultmetadata:languagecode') as
    | LanguageCodeOption['value']
    | null;

  if (languageCode === null) {
    return 'is-IS';
  }

  return languageCode;
};

const setDefaultLanguageCode = (value: LanguageCodeOption['value']) => {
  localStorage.setItem('tiro:submittranscript:defaultmetadata:languagecode', value);
};

type TranscriptEditorProps = {
  editable: boolean;
  initialSeekTime?: number;
  name?: string;
  selectedLanguage?: string;
  languageCodes: LanguageCodeOption[];
  version?: string;
} & RouteComponentProps &
  AuthContextProps;

interface TranscriptEditorState {
  editorState: EditorState;

  /**
   * This is true if the editor is waiting for something to be loaded or
   * processed.
   */
  waiting: boolean;

  speakerSelection?: SelectionState;
  speakerForEdit?: [string, transcript_pb.SpeakerInfo.AsObject];
  markSpeaker: boolean;
  markingSpeakers: boolean;
  segmentsToLabel?: {
    content: ContentState;
    selection: SelectionState;
  };
  segmentToEdit?: string;
  duration: number;

  showWaveform: boolean;
  showAnnotationWaveform: boolean;
  waveformData?: WaveformData;
  waveformBlockKey?: string;
  waveformContainer?: HTMLDivElement;
  waveformRegions?: RegionInfo[];

  playerMetadataLoaded: boolean;

  isLoading: boolean;
  progressPercent: number;
  error?: BasicError;
  captionsBlobUrl?: string;
  currentHypothesis: string;
  recording: boolean;
  recordingStarted: boolean;
  recordingStopped: boolean;
  saved: boolean;
  saving: boolean;
  languageCode: LanguageCodeOption['value'];
  editable: boolean;
  mediaReady: boolean;
  metadata: transcript_pb.TranscriptMetadata.AsObject;
  metadataActive: boolean;
  playbackRate: number;
  transcript?: transcript_pb.Transcript;
  transcriptPb?: transcript_pb.Transcript.AsObject;
  transcriptName?: string;
  transcriptVersion?: string;
  metadataEdited: boolean;
  transcriptEdited: boolean;
  waitingForExport: boolean;
  waitingForResponse: boolean;
  waitingForLoadSavedEdits: boolean;
  waitingForSaveEdits: boolean;
  segmentsIndex: number;
  disableHighlight: boolean;
  separateSegments: boolean;
}

const initialTranscriptEditorState: TranscriptEditorState = {
  editorState: EditorState.createEmpty(),
  waiting: true,
  duration: 0,
  progressPercent: 0,
  currentHypothesis: '',
  recording: false,
  recordingStarted: false,
  recordingStopped: false,
  saved: false,
  saving: false,
  languageCode: defaultLanguageCode(),
  editable: true,
  mediaReady: false,
  metadata: {
    fileType: transcript_pb.TranscriptMetadata.FileType.AUDIO,
    dictation: true,
    languageCode: defaultLanguageCode(),
    description: '',
    subject: '',
    originalUri: '',
    originalFilename: '',
    additionalMetadataMap: [],
    keywordsList: [],
    speakersMap: new Array<[string, transcript_pb.SpeakerInfo.AsObject]>(),
    waveformUri: '',
    originalCharLength: 0,
    originalByteLength: 0,
    dataSource: transcript_pb.TranscriptMetadata.DataSource.FILE_UPLOAD,
  },
  playbackRate: 1,
  showWaveform: false,
  showAnnotationWaveform: false,
  playerMetadataLoaded: false,
  markSpeaker: false,
  markingSpeakers: false,
  metadataActive: true,
  waitingForExport: false,
  waitingForResponse: false,
  waitingForLoadSavedEdits: false,
  waitingForSaveEdits: false,
  metadataEdited: false,
  transcriptEdited: false,
  transcriptPb: undefined,
  transcript: undefined,
  segmentsIndex: 0,
  disableHighlight: false,
  separateSegments: true,
  isLoading: false,
};

class TranscriptEditor extends React.Component<TranscriptEditorProps, TranscriptEditorState> {
  state: TranscriptEditorState;
  editorRef: React.RefObject<Editor>;
  videoRef: React.RefObject<HTMLVideoElement>;
  speechRef: React.MutableRefObject<StreamingSpeechHandler | null>;
  hiddenWaveformRef: React.RefObject<HTMLDivElement>;
  waveformRef: React.RefObject<HTMLDivElement>;
  private transcriptService: TranscriptService;
  private decorator: CompositeDecorator;
  hls: Hls;
  savePlaybackIntervalId?: number;
  keyboardUtils: KeyboardUtils;
  speech: SpeechEditorWrapper;

  private idleTimeout: any;

  constructor(props: TranscriptEditorProps) {
    super(props);

    this.editorRef = React.createRef();
    this.videoRef = React.createRef();
    this.speechRef = React.createRef();
    this.transcriptService = new TranscriptService({
      getIdToken: this.getIdToken,
    });
    this.keyboardUtils = new KeyboardUtils({
      onDownload: this.onDownload,
      onPlayback: this.onPlayback,
      onRecord: this.onRecord,
      onRestart: this.onRestart,
      onSave: this.onSave,
      onSeek: this.onSeek,
      onLabelSpeaker: this.onLabelSpeaker,
    });
    this.speech = new SpeechEditorWrapper({
      onAudioSrcUpdated: this.onAudioSrcUpdated,
      onError: this.onSpeechError,
      onHypothesis: this.onHypothesis,
      onResults: this.onResults,
    });

    this.hls = new Hls({ enableWorker: true });

    this.state = {
      ...initialTranscriptEditorState,
    };

    this.hiddenWaveformRef = React.createRef<HTMLDivElement>();
    this.waveformRef = React.createRef<HTMLDivElement>();
    this.decorator = new CompositeDecorator([
      {
        strategy: syncSegmentStrategy,
        component: DecoratorSegment,
        props: {
          editable: props.editable,
          playerRef: this.videoRef,
          onEditSpeaker: this.onEditSpeaker,
          getSpeakersMap: this.getSpeakersMap,
          setWaveformContainer: this.setWaveformContainer,
        },
      },
    ]);
  }

  private getIdToken = () => this.props.authCtx.accessToken;
  private getSpeakersMap = () => this.state.metadata.speakersMap;

  setWaveformContainer = (waveformContainer: HTMLDivElement, regions: RegionInfo[], blockKey: string) => {
    if (this.waveformRef.current) {
      if (waveformContainer === this.state.waveformContainer) {
        this.hideWaveform();
        return;
      }
      waveformContainer.appendChild(this.waveformRef.current);
      this.setState({
        waveformBlockKey: blockKey,
        waveformContainer,
        waveformRegions: regions,
      });
    }
  };

  onSubmitWaveform = (startTime: number, endTime: number) => {
    const { editorState, waveformBlockKey } = this.state;
    if (!waveformBlockKey) {
      return;
    }

    this.hideWaveform();

    const contentState = editorState.getCurrentContent();
    const newContentState = draftUtils.mergeBlockDataForKey(contentState, waveformBlockKey, { startTime, endTime });
    const newEditorState = EditorState.push(editorState, newContentState, 'change-block-data');
    this.onChange(newEditorState, this.forceUpdateEditor);
  };

  hideWaveform = () => {
    if (this.waveformRef.current && this.hiddenWaveformRef.current) {
      this.hiddenWaveformRef.current.appendChild(this.waveformRef.current);
    }
    this.setState({
      waveformBlockKey: undefined,
      waveformContainer: undefined,
      waveformRegions: undefined,
    });
  };

  onMetadataValueChange = (value: { [key: string]: any }) => {
    this.setState(
      (prevState) => ({
        metadata: {
          ...prevState.metadata,
          ...value,
        },
        metadataEdited: true,
      }),
      () => {
        clearTimeout(this.idleTimeout);
        this.idleTimeout = setTimeout(this.maybeSaveEdits, 10000);
      },
    );
  };

  getLangFromProps = () => {
    const lang = this.props.selectedLanguage && (this.props.selectedLanguage as LanguageCodeOption['value']);
    return lang;
  };

  componentDidMount = async () => {
    this.keyboardUtils.registerShortcutListeners();
    const languageCodeFromUrl = new URLSearchParams(this.props.location.search).get('langCode');

    if (languageCodeFromUrl) {
      this.setState((prevState) => ({
        languageCode: languageCodeFromUrl as LanguageCodeOption['value'],
        metadata: { ...prevState.metadata, languageCode: languageCodeFromUrl as LanguageCodeOption['value'] },
      }));
    }
    this.setState({
      transcriptName: this.props.name,
      transcriptVersion: this.props.version,
      editable: this.props.editable,
    });
    if (this.props.name) {
      const transcriptPb = await this.loadTranscriptFromRemote(this.props.name, this.props.version);
      this.handleExport('vtt_paint_on', true);
      this.populateBlocks(transcriptPb);

      if (transcriptPb.uri) {
        try {
          this.speech.getOriginalChunks(transcriptPb.uri);
          this.loadVideo(transcriptPb.uri);
        } catch (e) {
          console.error('could not load media file.');
        }
      }

      this.setState({
        transcriptName: this.props.name,
        transcriptVersion: this.props.version,
      });

      if (this.state.metadata.waveformUri) {
        axios
          .get(this.state.metadata.waveformUri)
          .then((response: AxiosResponse) => {
            const { data } = response;
            this.setState({ waveformData: data });
          })
          .catch((error: AxiosError) => {
            console.debug('error fetching peaks: ', error.message);
          });
      }
    } else {
      this.setState({ separateSegments: false });
    }
    if (this.videoRef.current) {
      this.videoRef.current.addEventListener('loadedmetadata', this.triggerMetadataLoaded);
    }
  };

  triggerMetadataLoaded = () => this.setState({ playerMetadataLoaded: true });

  componentWillUnmount = () => {
    if (this.speechRef.current && !this.speechRef.current.closed) {
      this.speechRef.current.close();
    }
    if (this.videoRef.current) {
      this.videoRef.current.removeEventListener('loadedmetadata', this.triggerMetadataLoaded);
    }
    clearTimeout(this.idleTimeout);
    this.keyboardUtils.deregisterShortcutListeners();
  };

  componentDidUpdate = async (prevProps: TranscriptEditorProps, prevState: TranscriptEditorState) => {
    if (prevProps.version !== this.props.version || prevProps.name !== this.props.name) {
      this.hideWaveform();
      if (this.props.name) {
        const transcriptPb = await this.loadTranscriptFromRemote(this.props.name, this.props.version);
        this.populateBlocks(transcriptPb);
      } else {
        if (this.props.location.pathname.endsWith('editor')) {
          this.onRestart();
          this.unloadVideo();
          this.setState({
            ...initialTranscriptEditorState,
          });
        }
      }
    }

    if (this.state.transcriptPb && prevState.transcriptPb) {
      if (this.state.transcriptPb.uri !== prevState.transcriptPb.uri) {
        const newUri = new URL(this.state.transcriptPb.uri);

        if (!prevState.transcriptPb.uri) {
          this.loadVideo(this.state.transcriptPb.uri);
        } else {
          const uri = new URL(prevState.transcriptPb.uri);

          let shouldLoad = false;
          if (newUri.pathname !== uri.pathname) {
            shouldLoad = true;
          } else {
            const expires = uri.searchParams.get('Expires');
            if (expires) {
              shouldLoad = new Date() > new Date(parseInt(expires) * 1000);
            }
          }
          shouldLoad && this.loadVideo(this.state.transcriptPb.uri);
        }
      }
    }
  };

  getTranscriptWordInfos = (transcriptPb: transcript_pb.Transcript.AsObject): SegmentInfo[] => {
    return transcriptPb.segmentsList.map((segment: transcript_pb.Transcript.TranscriptSegment.AsObject) => {
      const wordsList = (segment.wordsList || []).filter((word) => word.word.length > 0);
      const speaker = this.state.metadata.speakersMap.find(([key, value]) => key === segment.speakerId);
      let startTime: number;
      let endTime: number;
      if (segment.startTime && segment.endTime) {
        startTime = durationObjectToFloat(segment.startTime);
        endTime = durationObjectToFloat(segment.endTime);
      } else if (wordsList.length > 0) {
        startTime = durationObjectToFloat(wordsList[0].startTime);
        endTime = durationObjectToFloat(wordsList[wordsList.length - 1].endTime);
      } else {
        startTime = 0;
        endTime = 0;
      }

      return {
        speakerFullName: speaker ? speaker[1].fullName : undefined,
        speakerId: segment.speakerId,
        startTime,
        endTime,
        wordInfos: wordsList.map((word) => ({
          word: word.word,
          startTime: durationObjectToFloat(word.startTime),
          endTime: durationObjectToFloat(word.endTime),
          segmentStartTime: durationObjectToFloat(segment.startTime),
          segmentEndTime: durationObjectToFloat(segment.endTime),
        })),
      };
    });
  };

  populateBlocks = async (transcriptPb: transcript_pb.Transcript.AsObject) => {
    const editorState = await this.resetEditorState();
    const initialContentstate = editorState.getCurrentContent();

    // WordInfos from transcript protobuffer
    const segments = this.getTranscriptWordInfos(transcriptPb);

    // Populate current block with segment
    const contentStateWithSegments = await segments.reduce((acc: Promise<ContentState>, segment: SegmentInfo) => {
      return acc.then((newCs) => this.addSegmentToContentState(newCs, segment));
    }, Promise.resolve(initialContentstate));

    this.setState((prevState) => ({
      // NEW editorstate
      editorState: EditorState.createWithContent(contentStateWithSegments, this.decorator),
    }));
  };

  // Add wordInfos from transcript segment to `initialSelection` of `initialContentState`
  addSegmentToContentState = async (contentState: ContentState, segmentInfo: SegmentInfo): Promise<ContentState> => {
    // If current block is not empty, create a new one
    const currentBlock = contentState.getLastBlock();
    const newContentState =
      currentBlock.getLength() === 0
        ? contentState
        : Modifier.splitBlock(contentState, contentState.getSelectionAfter());

    const { speakerFullName, speakerId, wordInfos } = segmentInfo;
    const words = wordInfos.reduce((acc: string[], word: SegmentWordInfo) => acc.concat(word.word), []);
    const segment = words.join('');

    const contentStateWithEntity = newContentState.createEntity(EntityType.SYNC_SEGMENT, 'MUTABLE', {
      data: wordInfos,
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const cs = Modifier.replaceText(
      contentStateWithEntity,
      newContentState.getSelectionAfter(),
      segment,
      undefined,
      entityKey,
    );

    const segmentData = {
      startTime: segmentInfo.startTime,
      endTime: segmentInfo.endTime,
      speakerId,
      speakerFullName,
    };

    // Modify content state with new data
    const contentStateWithBlockData = Modifier.setBlockData(cs, cs.getSelectionAfter(), Map({ ...segmentData }));
    const contentStateWithBlockType = Modifier.setBlockType(
      contentStateWithBlockData,
      contentStateWithBlockData.getSelectionAfter(),
      EntityType.SYNC_SEGMENT,
    );
    return Promise.resolve(contentStateWithBlockType);
  };

  loadVideo = async (uri: string) => {
    console.debug('loadingvideo');
    if (this.videoRef.current) {
      if (uri.search('.m3u8') > -1) {
        console.debug('probably a HLS URI... acting accordingly');
        this.hls.loadSource(uri);
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.debug('manifest parsed');
          this.setState({ mediaReady: true });
        });
        this.hls.attachMedia(this.videoRef.current as HTMLVideoElement);
      } else {
        console.debug('setting media source.');
        this.videoRef.current.src = uri;
        this.setState({ mediaReady: true });
      }
    }
  };

  unloadVideo = async () => {
    if (this.videoRef.current) {
      this.hls.stopLoad();
      this.hls.detachMedia();
      this.videoRef.current.src = '';
    }
  };

  loadTranscriptFromRemote = async (name: string, version?: string): Promise<transcript_pb.Transcript.AsObject> => {
    return new Promise((resolve, reject) => {
      this.setState({
        waitingForLoadSavedEdits: true,
        waitingForResponse: true,
      });
      this.transcriptService
        .loadSegmentsFromRemote(name, version)
        .then((transcript) => {
          const transcriptPb = transcript.toObject();
          this.setState(
            (prevState) => ({
              metadata: {
                ...prevState.metadata,
                ...transcriptPb.metadata,
              },
              transcript,
              transcriptPb,
              waitingForLoadSavedEdits: false,
              waitingForResponse: false,
            }),
            () => resolve(transcriptPb),
          );
        })
        .catch((error) => {
          this.setState(
            {
              error: { ...this.state.error, message: error },
              waitingForResponse: false,
              waitingForLoadSavedEdits: false,
            },
            () => reject(error),
          );
        });
    });
  };

  onHypothesis = (currentHypothesis: string) => {
    this.setState({ currentHypothesis });
  };

  onResults = async (results: WordInfo[]): Promise<void> => {
    // TODO(rkjaran): This needs to be refactored and this eslint exception removed
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const editorState = this.state.editorState;
      const newContentState = await this.addSegmentToContentState(editorState.getCurrentContent(), {
        startTime: results[0].startTime,
        endTime: results[results.length - 1].endTime,
        wordInfos: results as SegmentWordInfo[],
      });
      this.onChange(EditorState.push(editorState, newContentState, 'insert-characters'));
      this.setState({ currentHypothesis: '' }, resolve);
    });
  };

  onAudioSrcUpdated = (audioURL: string) => {
    if (this.videoRef.current === null) {
      console.warn('no player');
      return;
    }
    const player = this.videoRef.current;
    player.src = audioURL;
  };

  onSpeechError = (error: string) => {
    this.setState({ error: { ...this.state.error, message: error } });
  };

  onRecord = () => {
    if (!this.state.metadata.dictation) {
      console.debug('not in dictation mode - recording disabled');
      return;
    }
    const { recording, recordingStarted, recordingStopped } = this.state;
    if (!recordingStarted && !recordingStopped) {
      this.startSpeech();
    } else if (recording) {
      this.pauseSpeech();
    } else {
      if (this.videoRef.current) {
        this.videoRef.current.pause();
      }
      this.resumeSpeech();
    }
  };

  pauseSpeech = () => {
    this.speech.pause();
    this.setState({ recording: false });
  };

  resumeSpeech = () => {
    this.speech.resume();
    this.setState({ recording: true });
  };

  startSpeech = async () => {
    // Obtaining the device labels only works if we already have recording
    // permission, and we have to include the label/deviceID when we request
    // the permission...
    const devices = await navigator.mediaDevices.enumerateDevices();
    const speechmike: MediaDeviceInfo | undefined = devices.find((device) => {
      return device.kind === 'audioinput' && device.label.toLowerCase().includes('speechmike');
    });
    const constraints = {
      audio: speechmike ? { deviceId: speechmike.deviceId } : true,
      video: false,
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    if (stream) {
      this.speech
        .start(this.state.languageCode)
        .then(() => {
          this.setState({
            recordingStarted: true,
            recording: true,
          });
        })
        .catch(() => {
          this.setState({
            error: {
              ...this.state.error,
              message: 'Gat ekki hafið upptöku. Prófaðu að endurglæða síðuna og reyna aftur.',
            },
          });
        });
    }
  };

  stopSpeech = () => {
    this.speech.stop();

    this.setState({
      recording: false,
      recordingStopped: true,
    });
  };

  onSeek = (back?: boolean) => {
    const { recording } = this.state;
    if (!recording && this.videoRef.current) {
      const current = this.videoRef.current.currentTime;
      const seek = Math.max(0, current + (back ? -2 : 2));
      this.videoRef.current.currentTime = seek;
    }
  };

  onPlayback = () => {
    if (!this.state.recording && this.videoRef.current) {
      if (this.videoRef.current.paused) {
        this.videoRef.current.play();
      } else {
        this.videoRef.current.pause();
      }
    }
  };

  editSpeaker = (speaker: transcript_pb.SpeakerInfo.AsObject, id: string) => {
    const {
      metadata: { speakersMap },
    } = this.state;
    const newSpeakersMap = speakersMap.map(([key, value]) => {
      const newValue: [string, transcript_pb.SpeakerInfo.AsObject] = key === id ? [key, speaker] : [key, value];
      return newValue;
    });

    this.onSubmitSpeaker(speaker, id, () =>
      this.setState(
        (prevState) => ({
          markSpeaker: false,
          speakerForEdit: undefined,
          metadata: {
            ...prevState.metadata,
            speakersMap: newSpeakersMap,
          },
          metadataEdited: true,
        }),
        this.forceUpdateEditor,
      ),
    );
  };

  onEditSpeaker = (key: string, id?: string) => {
    if (this.editorRef.current) {
      this.editorRef.current.blur();
    }

    if (id) {
      const speaker = this.state.metadata.speakersMap.find(([key, value]) => key === id);
      if (speaker) {
        this.setState({
          markSpeaker: true,
          speakerForEdit: speaker,
          segmentToEdit: key,
        });
      }
    } else {
      this.setState({
        markSpeaker: true,
        speakerForEdit: undefined,
        segmentToEdit: key,
      });
    }
  };

  removeSpeaker = async () => {
    const { editorState, segmentToEdit } = this.state;

    if (!segmentToEdit) {
      return;
    }
    const selection = editorState.getSelection();
    const newSelection = selection.merge({
      anchorKey: segmentToEdit,
      focusKey: segmentToEdit,
      anchorOffset: 0,
      focusOffset: 0,
      isBackward: false,
    }) as SelectionState;
    this.setState({
      markSpeaker: false,
      segmentToEdit: undefined,
      speakerForEdit: undefined,
    });

    const contentState = editorState.getCurrentContent();
    const newContentState = draftUtils.mergeBlockDataForSelection(contentState, newSelection, { speakerId: undefined });
    const newEditorState = EditorState.push(editorState, newContentState, 'change-block-data');
    this.onChange(newEditorState);
  };

  onLabelSpeaker = () => {
    const { editorState } = this.state;
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      this.setState({
        markSpeaker: true,
        segmentsToLabel: {
          content: editorState.getCurrentContent(),
          selection,
        },
      });
    } else {
      this.setState((prevState) => ({
        markingSpeakers: !prevState.markingSpeakers,
      }));
    }
  };

  handleMarkSegment = (content: ContentState, selection: SelectionState) => {
    this.setState({
      markSpeaker: true,
      segmentsToLabel: {
        content,
        selection,
      },
    });
  };

  onCancelSpeaker = () => {
    this.setState({
      markSpeaker: false,
      speakerForEdit: undefined,
      segmentToEdit: undefined,
    });
  };

  onSubmitSpeaker = async (speaker: transcript_pb.SpeakerInfo.AsObject, id?: string, callback?: () => void) => {
    let newSpeakerId;
    if (!id) {
      newSpeakerId = `speaker/${uuidv4()}`;
      const {
        metadata: { speakersMap },
      } = this.state;

      this.onMetadataValueChange({
        speakersMap: [...speakersMap, [newSpeakerId, speaker]],
      });
    } else {
      newSpeakerId = id;
    }

    const { editorState, segmentsToLabel, segmentToEdit } = this.state;

    if (segmentsToLabel) {
      const { content, selection } = segmentsToLabel;
      const newContentState = draftUtils.mergeBlockDataForSelection(content, selection, { speakerId: newSpeakerId });
      let newEditorState = EditorState.push(editorState, newContentState, 'change-block-data');
      newEditorState = EditorState.acceptSelection(newEditorState, selection);
      newEditorState = EditorState.forceSelection(newEditorState, newEditorState.getSelection());
      this.onChange(newEditorState);
    } else if (segmentToEdit) {
      const contentState = editorState.getCurrentContent();
      const newContentState = draftUtils.mergeBlockDataForKey(contentState, segmentToEdit, { speakerId: newSpeakerId });
      const newEditorState = EditorState.push(editorState, newContentState, 'change-block-data');
      this.onChange(newEditorState);
    } else {
      console.debug('no selection found while marking speaker');
    }

    this.setState(
      {
        markSpeaker: false,
        segmentsToLabel: undefined,
        speakerSelection: undefined,
      },
      callback,
    );
  };

  mergeBlockDataForSelection = (selection: SelectionState, data: any) => {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    const newContentState = Modifier.setBlockData(contentState, selection, Map(data));
    const newEditorState = EditorState.push(editorState, newContentState, 'change-block-data');
    this.onChange(newEditorState);
  };

  uploadAudio = async (): Promise<void> => {
    if (!this.state.transcriptName) {
      console.debug('no transcript name, ignoring upload audio request');
      return Promise.reject();
    }
    const { chunks, header } = await this.speech.getRecording();

    console.debug('uploading audio');
    return this.transcriptService.uploadAudio(this.state.transcriptName, chunks, header);
  };

  getCharEntityKeyAndSelection = (
    contentState: ContentState,
  ): { char: string; entityKey: string; selection: SelectionState } => {
    const selectionState = this.state.editorState.getSelection();
    const currentBlock = contentState.getBlockForKey(selectionState.getAnchorKey());
    const currentBlockText = currentBlock.getText();
    const endOfText = selectionState.getEndOffset();

    let offset = 0;
    for (let i = endOfText; i >= 0; i--) {
      const char = currentBlockText[i];
      if (char === ' ') {
        offset = i + 1;
        break;
      }
    }

    const entityKey = currentBlock.getEntityAt(offset);
    const emptySelectionState = SelectionState.createEmpty(selectionState.getAnchorKey());
    const newSelectionState = emptySelectionState.merge({
      focusOffset: offset + 1,
      anchorOffset: offset,
    }) as SelectionState;

    return { char: currentBlockText[offset], entityKey, selection: newSelectionState };
  };

  onSave = () => {
    if (!this.state.recording) {
      if (!this.state.recordingStopped) {
        this.stopSpeech();
      }
      this.maybeSaveEdits();
    }
  };

  onLanguageChange = (_: any, target: any) => {
    const languageCode = target.value;
    setDefaultLanguageCode(languageCode);
    this.setState((prevState) => ({
      languageCode,
      metadata: { ...prevState.metadata, languageCode },
    }));
  };

  onChange = (editorState: EditorState, callback?: (editorState: EditorState) => void) => {
    const isUndo = editorState.getLastChangeType() === 'undo';
    isUndo && this.state.waveformRegions && this.hideWaveform();

    this.setState(
      (prevState) => ({
        editorState,
        transcriptEdited:
          prevState.transcriptEdited || // if !transcriptEdited, compare
          prevState.editorState.getCurrentContent() !== editorState.getCurrentContent(),
      }),
      () => {
        const { transcriptEdited } = this.state;
        if (transcriptEdited) {
          clearTimeout(this.idleTimeout);
          this.idleTimeout = setTimeout(this.maybeSaveEdits, 10000);
        }
        if (callback) {
          callback(editorState);
        }
      },
    );
  };

  forceUpdateEditor = () => {
    this.setState((prevState) => {
      let newEditorState = EditorState.createWithContent(prevState.editorState.getCurrentContent(), this.decorator);
      newEditorState = EditorState.set(newEditorState, {
        selection: prevState.editorState.getSelection(),
        undoStack: prevState.editorState.getUndoStack(),
        redoStack: prevState.editorState.getRedoStack(),
        lastChangeType: prevState.editorState.getLastChangeType(),
      });
      return {
        editorState: newEditorState,
      };
    });
  };

  onDurationChange = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    const { duration } = event.target as HTMLAudioElement;
    this.setState({ duration });
  };

  onDownload = () => {
    const textContent = this.state.editorState.getCurrentContent().getPlainText('');
    const textarea = document.createElement('textarea');
    textarea.value = textContent;
    textarea.style.position = 'fixed';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  onRestart = () => {
    if (!this.state.metadata.dictation) {
      console.debug('not in dictation mode - restart disabled');
      return;
    }

    if (this.state.recording) {
      this.speech.pause();
    }
    if (!this.state.recordingStopped) {
      this.stopSpeech();
    }
    if (this.videoRef.current) {
      this.videoRef.current.src = '';
    }
    this.setState((prevState) => {
      return {
        ...initialTranscriptEditorState,
        editorState: EditorState.createEmpty(prevState.editorState.getDecorator()),
      };
    });
  };

  resetEditorState = (): Promise<EditorState> => {
    return new Promise((resolve) => {
      this.setState(
        (prevState) => ({
          editorState: EditorState.createEmpty(prevState.editorState.getDecorator()),
        }),
        () => resolve(this.state.editorState),
      );
    });
  };

  setupSavePlayback = () => {
    const playbackPosition = this.props.initialSeekTime
      ? this.props.initialSeekTime
      : parseFloat(localStorage.getItem(`Transcript/${this.props.name}/PlaybackPosition`) || '0');
    if (this.videoRef.current) {
      this.videoRef.current.currentTime = playbackPosition;
      this.videoRef.current.addEventListener('seeked', this.savePlaybackPositionLocally);
      this.savePlaybackIntervalId = window.setInterval(this.savePlaybackPositionLocally, 10000);

      /* XXX: this doesn't really belong here */
      this.videoRef.current.playbackRate = this.state.playbackRate;
    }
  };

  savePlaybackPositionLocally = () => {
    const currentTime = this.videoRef.current?.currentTime.toString();
    if (currentTime) {
      localStorage.setItem(`Transcript/${this.props.name}/PlaybackPosition`, currentTime);
    }
  };

  setPlaybackRate = (playbackRate: number) => {
    if (this.videoRef.current) {
      this.videoRef.current.playbackRate = playbackRate;
    }
    this.setState({ playbackRate });
  };

  handleExport = (format: TranscriptExportFormat, noDownload?: boolean) => {
    if (!this.state.transcriptName) {
      console.debug('export failed, no transcript name');
      return;
    }
    console.log('Export started - setting waitingForExport to true', this.state.waitingForExport);
    if (format === 'txt') {
      const textContent = this.state.editorState.getCurrentContent().getPlainText('');
      if (noDownload) {
        const textarea = document.createElement('textarea');
        textarea.value = textContent;
        textarea.style.position = 'fixed';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      } else {
        console.log('Here');
        const blob = new Blob([textContent], { type: 'text/vtt' });
        const url = URL.createObjectURL(blob);
        const hf = document.createElement('a');
        hf.style.display = 'none';
        hf.href = url;
        hf.download = `transcript-${this.state.metadata.subject || this.state.transcriptName}.txt`;
        document.body.appendChild(hf);
        hf.click();
        URL.revokeObjectURL(url);
        hf.remove();
      }
    } else {
      this.setState({ waitingForExport: true });
      this.transcriptService
        .exportTranscript(this.state.transcriptName, format, this.state.metadata.subject, noDownload)
        .then((captionsBlobUrl?: string) => {
          this.setState({
            captionsBlobUrl,
            waitingForExport: false,
          });
        })
        .catch((error) => {
          this.setState({
            error: { ...this.state.error, message: error, type: 'export' },
            waitingForExport: false,
          });
        });
    }
    console.log('Finally the state is', this.state.waitingForExport);
  };

  // Upload local edits if there are any, returns immediately if there are none
  maybeSaveEdits = async () => {
    if (!this.state.editable) {
      console.debug('cannot save while anonymous, ignoring event');
      return;
    }
    if (this.state.waitingForSaveEdits || this.state.waitingForResponse || this.state.waitingForLoadSavedEdits) {
      console.debug('maybe_save_edits', "waiting for other actions, can't save.");
      return;
    }

    if (!this.state.transcriptEdited && !this.state.metadataEdited) {
      console.debug('maybe_save_edits', 'no edits since last save ... ignoring event.');
      return;
    }

    const { duration, metadata, transcript, editorState } = this.state;

    // If it's a new file, create transcript and retry
    if (!transcript) {
      try {
        const name = await this.transcriptService.createTranscript(duration, metadata);
        await this.loadTranscriptFromRemote(name);
        this.setState({ transcriptName: name });
        await this.maybeSaveEdits();
        this.props.history.push(name);
      } catch (error) {
        console.debug(`error creating transcript, ${error}`);
      }

      return;
    }

    this.setState({ waitingForSaveEdits: true });

    // Save edits
    try {
      const updatedTranscript = await this.transcriptService.updateTranscript(
        transcript,
        editorState,
        metadata,
        this.state.transcriptPb?.annotationsList,
      );
      this.setState({ waitingForSaveEdits: false });

      const transcriptPb = updatedTranscript.toObject();
      this.setState({
        metadataEdited: false,
        transcriptEdited: false,
        transcript: updatedTranscript,
        transcriptPb,
      });

      if (!transcriptPb.uri && this.state.recordingStarted && this.state.recordingStopped) {
        await this.uploadAudio();
      }
    } catch (error) {
      this.setState({
        waitingForSaveEdits: false,
        error: { ...this.state.error, message: error },
      });
    }

    if (this.state.metadata.fileType !== transcript_pb.TranscriptMetadata.FileType.AUDIO) {
      this.handleExport('vtt_paint_on', true);
    }
  };

  handleJoin = () => {
    const { editorState } = this.state;
    const { content, selection } = draftUtils.mergeSegments(editorState);
    let newEditorState = EditorState.push(editorState, content, 'remove-range');
    newEditorState = EditorState.acceptSelection(newEditorState, selection);
    newEditorState = EditorState.forceSelection(newEditorState, newEditorState.getSelection());
    this.onChange(newEditorState);
  };

  handleAnnotationsUpdate = (annotations: Annotation.AsObject[]) => {
    this.setState(
      (prevState) => ({
        transcriptEdited: true,
        transcriptPb: {
          ...prevState.transcriptPb,
          annotationsList: [...annotations],
        } as transcript_pb.Transcript.AsObject,
      }),
      () => {
        const { transcriptEdited } = this.state;
        if (transcriptEdited) {
          clearTimeout(this.idleTimeout);
          this.idleTimeout = setTimeout(this.maybeSaveEdits, 10000);
        }
      },
    );
  };

  dismissError = () => {
    this.setState({ error: undefined });
  };

  render() {
    const playbackMenu: MenuProps = {
      onClick: ({ key }) => this.setPlaybackRate(Number(key)),
      items: [
        { key: 0.25, label: '0.25x' },
        { key: 0.5, label: '0.5x' },
        { key: 0.75, label: '0.75x' },
        { key: 1.0, label: '1.0x' },
        { key: 1.25, label: '1.25x' },
        { key: 1.5, label: '1.5x' },
      ],
    };

    const selection = this.state.editorState.getSelection();
    const selectionActive = selection.getEndOffset() !== selection.getStartOffset();

    return (
      <Layout
        headerComponent={
          <div>
            <EditorHeaderControls
              onMetadataValueUpdate={this.onMetadataValueChange}
              metadata={this.state.metadata}
              onLanguageChange={this.onLanguageChange}
              isDictation={this.state.metadata.dictation}
              languageCodes={this.props.languageCodes}
              selectedLanguageCode={this.state.languageCode}
              keyboardUtils={this.keyboardUtils}
              canSave={this.state.transcriptEdited || this.state.metadataEdited}
              onStart={this.startSpeech}
              onStop={this.stopSpeech}
              onPause={this.pauseSpeech}
              onResume={this.resumeSpeech}
              onSave={this.onSave}
              onExport={this.handleExport}
              onRestart={this.onRestart}
              recording={this.state.recording}
              started={this.state.recordingStarted}
              stopped={this.state.recordingStopped}
              saved={this.state.saved}
              saving={this.state.saving}
              transcriptPb={this.state.transcriptPb}
              waitingForExport={this.state.waitingForExport}
              error={this.state.error ? this.state.error : undefined}
              dismissError={this.dismissError}
              version={this.state.transcriptPb ? this.state.transcriptPb.version : undefined}
              waitingForSaveEdits={this.state.waitingForSaveEdits}
            />
            <Space.Compact>
              <Button
                title='Litun'
                icon={<HighlightOutlined />}
                type={!this.state.disableHighlight ? 'primary' : 'default'}
                ghost={!this.state.disableHighlight}
                onClick={() => {
                  this.setState((prevState) => ({ disableHighlight: !prevState.disableHighlight }));
                  this.forceUpdateEditor();
                }}
              />
              <Button
                title='Aðskilja parta'
                icon={<VerticalAlignMiddleOutlined />}
                disabled={!!this.state.markingSpeakers}
                type={this.state.separateSegments ? 'primary' : 'default'}
                ghost={this.state.separateSegments}
                onClick={() => {
                  if (this.state.separateSegments) {
                    this.hideWaveform();
                  }
                  this.setState((prevState) => ({
                    separateSegments: !prevState.separateSegments,
                  }));
                }}
              />
              <Button
                icon={<TagOutlined />}
                disabled={!this.state.editable || !this.state.separateSegments || !selectionActive}
                type={this.state.markingSpeakers ? 'primary' : 'default'}
                ghost={this.state.markingSpeakers}
                onClick={this.onLabelSpeaker}
              >
                Merkja mælanda
              </Button>
              <Button
                icon={<PlusOutlined />}
                disabled={!this.state.editable || !this.state.separateSegments}
                onClick={this.handleJoin}
              >
                Sameina parta
              </Button>
              <Button
                title={`${this.state.showAnnotationWaveform ? 'Fela' : 'Sýna'} merkingarlag`}
                icon={
                  <img
                    src={`/waveform-${this.state.showAnnotationWaveform ? 'minus' : 'plus'}.svg`}
                    width={20}
                    height={20}
                  />
                }
                type={this.state.showAnnotationWaveform ? 'primary' : 'default'}
                ghost={this.state.showAnnotationWaveform}
                onClick={() =>
                  this.setState((prevState) => ({ showAnnotationWaveform: !prevState.showAnnotationWaveform }))
                }
              />
            </Space.Compact>
          </div>
        }
      >
        {this.state.markSpeaker && (
          <SpeakerModal
            onCancel={this.onCancelSpeaker}
            onEdit={this.editSpeaker}
            onRemove={this.removeSpeaker}
            onSubmit={this.onSubmitSpeaker}
            speakersMap={this.state.metadata.speakersMap}
            speakerForEdit={this.state.speakerForEdit}
          />
        )}

        <div className={`editor-container`}>
          <div className={`editor-error-container ${!this.state.error ? 'no-error' : ''}`}>
            {this.state.error ? (
              <ErrorMessage header='Villa!' message={this.state.error.message} onDismiss={this.dismissError} />
            ) : null}
          </div>

          <Row gutter={16} className={'editor-wrapper-container'}>
            <Col xs={24} md={10} lg={8} className={'editor-data-container'}>
              <Space direction='vertical' style={{ width: '100%', position: 'sticky', top: '1rem' }}>
                <div className={'editor-media-container'}>
                  <VideoPlayer
                    ready={this.state.mediaReady ? 'true' : undefined}
                    ref={this.videoRef}
                    onDurationChange={this.onDurationChange}
                    onLoadedMetadata={this.setupSavePlayback}
                    video={
                      this.state.transcriptPb &&
                      this.state.transcriptPb.metadata &&
                      this.state.transcriptPb.metadata.fileType === TranscriptMetadata.FileType.VIDEO
                        ? 'yes'
                        : undefined
                    }
                  >
                    {this.state.transcriptPb && this.state.transcriptPb.metadata && this.state.captionsBlobUrl ? (
                      <track
                        default={!this.props.authCtx.accessToken}
                        kind='captions'
                        lang={this.state.transcriptPb.metadata.languageCode}
                        label='Íslenska'
                        src={this.state.captionsBlobUrl}
                      />
                    ) : null}
                  </VideoPlayer>
                </div>

                <div className='help-container'>
                  <Space>
                    <Button
                      type={this.state.metadataActive ? 'primary' : 'default'}
                      ghost={this.state.metadataActive}
                      onClick={() => this.setState((prevState) => ({ metadataActive: !prevState.metadataActive }))}
                      icon={<InfoCircleOutlined />}
                    >
                      Lýsigögn
                    </Button>

                    <Dropdown menu={playbackMenu}>
                      <Button>
                        Hraði {this.state.playbackRate}x <DownOutlined />
                      </Button>
                    </Dropdown>
                  </Space>
                </div>

                <div ref={this.hiddenWaveformRef} className={'hidden-waveform'}>
                  {this.state.metadata.waveformUri && this.state.mediaReady && (
                    <div ref={this.waveformRef}>
                      <Waveform
                        duration={this.state.duration}
                        playerRef={this.videoRef}
                        onClose={this.hideWaveform}
                        onSubmit={this.onSubmitWaveform}
                        regions={this.state.waveformRegions}
                        waveformData={this.state.waveformData}
                      />
                    </div>
                  )}
                </div>

                <div className={`metadata-container ${this.state.metadataActive ? 'active' : ''}`}>
                  <TranscriptMetadataDisplay
                    editable={this.state.editable}
                    name={this.state.transcriptPb && this.state.transcriptPb.name}
                    version={this.state.transcriptPb && this.state.transcriptPb.version}
                    metadata={this.state.metadata}
                    onChange={(metadata: TranscriptMetadata.AsObject) => this.onMetadataValueChange(metadata)}
                  />
                </div>
              </Space>
            </Col>

            <Col xs={24} md={14} lg={16} className={'document-container'}>
              {this.state.waveformData && (
                <AnnotationWaveform
                  hide={!this.state.showAnnotationWaveform}
                  annotations={this.state.transcriptPb?.annotationsList || []}
                  waveformData={this.state.waveformData}
                  playerRef={this.videoRef}
                  onUpdate={this.handleAnnotationsUpdate}
                />
              )}

              <Space direction='vertical' style={{ width: '100%' }}>
                <div
                  className={`transcript-container ${!this.state.separateSegments ? 'inline' : ''}`}
                  onClick={() => this.editorRef.current && this.editorRef.current.focus()}
                >
                  <DraftEditorWrapper
                    ref={this.editorRef}
                    editable={this.props.editable}
                    editorState={this.state.editorState}
                    isRecording={this.state.recording}
                    keyboardUtils={this.keyboardUtils}
                    onChange={this.onChange}
                    playerRef={this.videoRef}
                    disableHighlight={this.state.disableHighlight}
                    markingSpeakers={this.state.markingSpeakers}
                    handleMarkSegment={this.handleMarkSegment}
                  />
                  <span className='editor-hypothesis'>{this.state.currentHypothesis}</span>
                </div>
              </Space>
            </Col>
          </Row>
        </div>
      </Layout>
    );
  }
}

export const AnonymousEditor = withAnonymousAuth(withRouter(TranscriptEditor));
export default withAuth(withRouter(TranscriptEditor));
